import { useState, useCallback, useRef } from 'react';
import useRequirementAnalysis from './useRequirementAnalysis';

/**
 * Product Knowledge–scoped controller: modal state, analyze action, snackbar on failure.
 * Keeps ProductKnowledge.js free of response parsing and analysis formatting.
 */
export default function useProductKnowledgeRequirementAnalysis({ projectId, setSnackbar }) {
  const [open, setOpen] = useState(false);
  const [item, setItem] = useState(null);
  const [result, setResult] = useState(null);
  const cacheRef = useRef({});
  const { analyzeRequirement, loading } = useRequirementAnalysis({ projectId });

  const close = useCallback(() => {
    setOpen(false);
    setItem(null);
  }, []);

  const runAnalysis = useCallback(
    async (rowItem, { forceRefresh = false } = {}) => {
      if (!projectId || !rowItem?.knowledge_id) return;
      const kid = rowItem.knowledge_id;
      setItem(rowItem);
      setOpen(true);

      if (!forceRefresh && cacheRef.current[kid]) {
        setResult(cacheRef.current[kid]);
      } else {
        setResult(null);
      }

      try {
        const data = await analyzeRequirement(kid, { forceRefresh });
        setResult(data);
        cacheRef.current[kid] = data;
      } catch (e) {
        setOpen(false);
        setItem(null);
        setSnackbar({
          open: true,
          message: e.message || 'Requirement analysis failed.',
          severity: e.code === 'timeout' ? 'warning' : 'error',
          persist: true,
        });
      }
    },
    [projectId, analyzeRequirement, setSnackbar],
  );

  const onAnalyzeRequirement = useCallback(
    (rowItem) => runAnalysis(rowItem, { forceRefresh: false }),
    [runAnalysis],
  );

  const onRefreshRequirementAnalysis = useCallback(
    (rowItem) => runAnalysis(rowItem || item, { forceRefresh: true }),
    [runAnalysis, item],
  );

  return {
    requirementAnalysisLoading: loading,
    onAnalyzeRequirement,
    requirementAnalysisModalProps: {
      open,
      onClose: close,
      loading,
      data: result,
      sourceItem: item,
      onRefresh: onRefreshRequirementAnalysis,
    },
  };
}
